import clipboardCopy from "clipboard-copy";
import _ from "lodash";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { Col, Container, Row, Spinner, Table } from "react-bootstrap";
import { FaCircle, FaCopy } from "react-icons/fa";
import { DialogContext } from "../../../../../store/context/DialogContext";
import { doGET } from "../../../../../util/HttpUtil";
import { FORM_TYPE } from "../../IPlushelper";
import "./style.css";
import { buildKycVerifyUrl } from "../../../../../components/Broker/kycLink";
import { buildAutomationChildTestId, buildAutomationValueTestId, resolveAutomationTestId } from "../../../../../components/InputField/testId";

const FORM_TYPE_TO_LINK_TYPE = {
    BUSINESS_PLUS: "BUSINESS_PLUS",
    BUSINESS_PRIME: "BUSINESS_PLUS",
    INDIVIDUAL_PLUS: "INDIVIDUAL_PLUS",
    INDIVIDUAL_PRIME: "INDIVIDUAL_PLUS",
    BUSINESS: "BUSINESS",
};

const headerLabels = {
    [FORM_TYPE.IndividualPlus]: "KYC Status",
    [FORM_TYPE.BusinessPlus]: "Users/Operator/Shareholder KYC Status",
    [FORM_TYPE.IndividualPrime]: "KYC Status",
    [FORM_TYPE.BusinessPrime]: "Users/Operator/Shareholder KYC Status",
    [FORM_TYPE.Business]: "Users/Operator/Shareholder/Control Prong KYC Status",
}
const stepStatusOptions = [
    { label: "Pending", value: 0, backgroundColor: "#F8D76E", keyColor: "#CD9941", },
    { label: "Completed", value: 1, backgroundColor: "#B2D8B2", keyColor: "#3D5941" },
    { label: "Pending", value: 2, backgroundColor: "#F8D76E", keyColor: "#CD9941", },
    { label: "Completed", value: 3, backgroundColor: "#B2D8B2", keyColor: "#3D5941" },
    { label: "Rejected", value: 4, backgroundColor: "#F1A7A6", keyColor: "#8B4A4A" },
    { label: "Completed", value: 5, backgroundColor: "#B2D8B2", keyColor: "#3D5941" },
];

const mapFlowStatusToLabel = (flowStatusValue) => {
    const found = (stepStatusOptions ?? [])?.find((o) => o?.value === flowStatusValue);
    return found ? found : { label: "Pending", value: 0, backgroundColor: "#F8D76E", keyColor: "#4A4A4A", };
};

const NOT_REQUIRED_STATUS = {
    label: 'Not required',
    value: -1,
    backgroundColor: '#E5E7EB',
    keyColor: '#4B5563',
};

const BusinessPlusKYCStatus = ({
    isKycRequired = true,
    formType,
    applicationId,
    onAllKYCComplete,
    allKYCComplete,
    requiredUboIds,
    testId,
}) => {

    const [loading, setLoading] = useState(true);
    const [uboData, setUboData] = useState([]);
    const [ondatoData, setOndatoData] = useState([]);
    const [applicationForm, setApplicationForm] = useState(null);
    const { showMessage } = useContext(DialogContext);
    const resolvedTestId = resolveAutomationTestId(testId, "kyc-status");
    const requiredIdsSet = useMemo(() => {
        if (Array.isArray(requiredUboIds)) {
            return new Set(requiredUboIds);
        }
        return null;
    }, [requiredUboIds]);

    const combinedUBO = useMemo(() => {
        const ondatoGrouped = _.groupBy(ondatoData, "ubo_id");

        return (uboData ?? [])?.map((ubo) => {
            const reports = ondatoGrouped[ubo?._id] || [];
            const latest = _.maxBy(reports, (r) => r?.seq);
            const requiresKyc = requiredIdsSet ? requiredIdsSet.has(ubo?._id) : true;

            const kycStatus = requiresKyc
                ? latest
                    ? mapFlowStatusToLabel(latest?.flowStatus)
                    : { label: "Pending", value: 0, backgroundColor: "#F8D76E", keyColor: "#4A4A4A", }
                : NOT_REQUIRED_STATUS;

            return {
                ...ubo,
                requiresKyc,
                finalKycStatus: kycStatus,
                latestOndato: latest,
            };
        });
    }, [uboData, ondatoData, requiredIdsSet]);

    useEffect(() => {
        const allComplete = combinedUBO.filter(ubo => ubo?.finalKycStatus?.value !== -1).every(ubo =>
            [1, 3, 5].includes(ubo?.finalKycStatus?.value)
        );
        if (!isKycRequired) {
            if (onAllKYCComplete) {
                onAllKYCComplete(true);
            }
        } else if (onAllKYCComplete) {
            onAllKYCComplete(allComplete);
        }
    }, [combinedUBO]);

    useEffect(() => {
        const fetchUBO = async () => {
            try {
                const res = await doGET(
                    `/api/ubo/grid?application_id=${applicationId}&rows=-1`
                );
                if (res?.status === 200) {
                    const uboData = res?.data?.rows?.filter(individual => {
                        if ([FORM_TYPE.IndividualPrime, FORM_TYPE.IndividualPlus].includes(formType)) {
                            return true
                        }
                        if (individual?.isAuthorizedUser) {
                            return true
                        }
                        if (individual?.isControlPerson) {
                            return true
                        }
                        if (individual?.isShareHolder) {
                            return parseFloat(individual?.percentOwner) >= 25
                        }
                        return false;
                    })
                    setUboData(uboData || []);
                }
            } catch (err) {
                console.error("Failed to fetch UBO data:", err);
            }
        };

        const fetchOndato = async () => {
            try {
                const res = await doGET(
                    `/api/ondato/grid?form_id=${applicationId}&sortBy=seq&sortAsc=false&rows=-1`
                );
                if (res?.status === 200) {
                    setOndatoData(res?.data?.rows || []);
                }
            } catch (err) {
                console.error("Failed to fetch Ondato data:", err);
            }
        };

        const fetchApplicationForm = async () => {
            try {
                const res = await doGET(`/api/application-form/detail?id=${applicationId}`);
                if (res?.status === 200) {
                    setApplicationForm(res?.data || null);
                }
            } catch (err) {
                setApplicationForm(null);
            }
        };

        const loadAll = async () => {
            setLoading(true);
            await Promise.all([fetchUBO(), fetchOndato(), fetchApplicationForm()]);
            setLoading(false);
        };

        if (applicationId) {
            loadAll();
        }
    }, [applicationId, formType]);

    // Build the link for each UBO
    const generateKycLink = (ubo) => {
        const { _id: uboId, email } = ubo;
        const linkType = FORM_TYPE_TO_LINK_TYPE?.[formType] ?? FORM_TYPE.BusinessPlus;

        if (applicationForm?.brokerId) {
            return buildKycVerifyUrl({ token: uboId, email });
        }

        const currentHost = window.location.hostname;
        const applyHost = _.toLower(currentHost) || "apply.fvbank.us";
        return `https://${applyHost}/verify-individual/?form_id=${applicationId}&ubo_id=${uboId}&email=${email}&formType=${linkType}`;
    };

    const handleCopyLink = (link) => {
        if (!link) return;
        clipboardCopy(link);
        showMessage('Link copied to clipboard!');
    };

    if (loading) {
        return (
            <div
                className="d-flex justify-content-center align-items-center fv_bplus_operators_loader"
                style={{ minHeight: 200 }}
                data-testid={buildAutomationChildTestId(resolvedTestId, "loading")}
            >
                <Spinner animation="border" />
            </div>
        );
    }

    const hasExplicitRequirements = Array.isArray(requiredUboIds);

    if (!combinedUBO?.length) {
        return <p className="fv_bplus_operators_noData" data-testid={buildAutomationChildTestId(resolvedTestId, "empty")}>No user/operator/shareholder data found.</p>;
    }

    if ([FORM_TYPE.IndividualPlus, FORM_TYPE.IndividualPrime]?.includes(formType) && allKYCComplete) {
        return null
    }

    if (!isKycRequired) {
        onAllKYCComplete(true);
        return null
    }

    return (
        <Container fluid className="fv_bplus_operators_wrapper py-4" data-testid={resolvedTestId}>

            <Row data-testid={buildAutomationChildTestId(resolvedTestId, "content")}>
                <Col>
                    <h2 className="fv_bplus_operators_title mb-4" data-testid={buildAutomationChildTestId(resolvedTestId, "title")}>
                        {headerLabels?.[formType] ?? "KYC Status"}
                    </h2>
                    <Table
                        bordered
                        hover
                        responsive
                        className="fv_bplus_operators_table shadow-sm"
                        data-testid={buildAutomationChildTestId(resolvedTestId, "table")}
                    >
                        <thead>
                            <tr className="fv_bplus_operators_table_head">
                                <th className="fv_bplus_operators_table_th">Name</th>
                                <th className="fv_bplus_operators_table_th">Email</th>
                                <th className="fv_bplus_operators_table_th">KYC Status</th>
                                <th className="fv_bplus_operators_table_th">Link to complete KYC</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(combinedUBO ?? [])?.map((ubo) => {
                                const link = ubo?.requiresKyc ? generateKycLink(ubo) : null;
                                return (
                                    <tr key={ubo?._id} className="fv_bplus_operators_table_tr" data-testid={buildAutomationChildTestId(resolvedTestId, "row")} data-record-id={ubo?._id}>
                                        <td className="fv_bplus_operators_table_td" data-testid={buildAutomationChildTestId(resolvedTestId, "row-name")}>
                                            {ubo?.firstName ?? ""}  {ubo?.middleName ?? ""} {ubo?.lastName ?? ""}
                                        </td>
                                        <td className="fv_bplus_operators_table_td" data-testid={buildAutomationChildTestId(resolvedTestId, "row-email")}>{ubo?.email}</td>
                                        <td style={{ color: ubo?.finalKycStatus?.keyColor }} className="fv_bplus_operators_table_td" data-testid={buildAutomationValueTestId(buildAutomationChildTestId(resolvedTestId, "row-status"), ubo?.finalKycStatus?.label ?? "pending")}>
                                            <FaCircle style={{ color: ubo?.finalKycStatus?.backgroundColor }} />    {ubo?.finalKycStatus?.label ?? "Pending"}
                                        </td>
                                        <td className="fv_bplus_operators_table_td" data-testid={buildAutomationChildTestId(resolvedTestId, "row-kyc-link")}>
                                            {link ? (
                                                <div className="d-flex align-items-center">
                                                    <a
                                                        href={link}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="fv_bplus_operators_link me-2"
                                                        data-testid={buildAutomationChildTestId(resolvedTestId, "open-kyc-link")}
                                                    >
                                                        Open Link
                                                    </a>
                                                    <FaCopy
                                                        className="fv_bplus_operators_copyIcon"
                                                        style={{ cursor: "pointer" }}
                                                        data-testid={buildAutomationChildTestId(resolvedTestId, "copy-kyc-link")}
                                                        onClick={() => handleCopyLink(link)}
                                                    />
                                                </div>
                                            ) : (
                                                <span className="text-muted">KYC not required</span>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                </Col>
            </Row>
        </Container>
    );
};

export default BusinessPlusKYCStatus;
